<template>
  <v-container fluid fill-height style="height:calc(100vh - 64px)">
    <v-row style="height: 100%; width: 100%">
      <v-col cols="12" md="6" style="height: 100%">
        <v-card ref="content" height="100%" id="project-card" :style="getCardGrid">
          <v-row ref="title" no-gutters>
            <v-col class="pa-3 d-flex justify-space-between align-center" cols="12">
              <span class="headline">{{ $i18n.t('dashboard.offers.title') }}</span>
              <span>
                <v-badge
                  :value="unreadOffersCount > 0"
                  class="float-right"
                  color="rgb(180,0,0)"
                  right
                  overlap
                >
                  <template v-slot:badge>
                    <span v-text="unreadOffersCount" />
                  </template>
                  <v-menu :close-on-content-click="false">
                    <template v-slot:activator="{on, attrs}">
                      <v-btn
                        class="float-right mr-1"
                        fab
                        dark
                        :title="$i18n.t('default.offersNotifications')"
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon dark>mdi-bell</v-icon>
                      </v-btn>
                    </template>
                    <v-list
                      @scroll.native="scroll($event)"
                      style="overflow-y:auto;"
                      dense
                      height="300px"
                      width="500px"
                    >
                      <v-subheader class="justify-space-between">
                        <span>{{$i18n.t('default.offersNotifications')}}</span>
                        <span
                          v-if="unreadOffersCount > 0"
                          @click.stop="markAsReadAll()"
                          style="cursor:pointer"
                        >
                          <u>{{$i18n.t('default.markAllNotifications')}}</u>
                        </span>
                      </v-subheader>
                      <v-list-item-group v-if="offersHistory.length > 0">
                        <v-list-item
                          style="max-height: 30px"
                          @click.stop="markAsReadSingle(offer, true)"
                          v-for="offer in offersHistory"
                          :key="offer.history_id"
                        >
                          <v-list-item-content>
                            <v-list-item-title
                              v-bind:class="{'notRead': !offer.read, 'wrap-text': true}"
                            >
                              <span
                                :title="offerNotificationTitle(offer)"
                                style="font-size: 75%"
                              >{{offerNotificationTitle(offer)}}</span>
                            </v-list-item-title>
                            <v-list-item-subtitle>
                              <span
                                style="font-size: 75%"
                              >{{offerNotificationSubtitle(offer.imported_at_datetime)}}</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                          <v-list-item-icon>
                            <v-icon
                              v-if="!offer.read"
                              small
                              class="float-right pl-5"
                              @click.stop="markAsReadSingle(offer, false)"
                            >mdi-check</v-icon>
                          </v-list-item-icon>
                        </v-list-item>
                      </v-list-item-group>
                      <v-list-item-group
                        v-else-if="offersHistory.length === 0 && !offersHistoryLoading"
                      >
                        <v-list-item>{{$i18n.t('default.noNotifications')}}</v-list-item>
                      </v-list-item-group>
                      <v-list-item-group v-else>
                        <v-list-item>
                          <v-progress-circular indeterminate color="primary" />
                        </v-list-item>
                      </v-list-item-group>
                    </v-list>
                  </v-menu>
                </v-badge>
                <v-badge :value="filtersLength > 0" class="float-right" color="red" right overlap>
                  <template v-slot:badge>
                    <span v-text="filtersLength" />
                  </template>
                  <v-btn
                    class="float-right btn--filter mr-1"
                    fab
                    dark
                    :title="$i18n.t('dashboard.addFilters')"
                    small
                    color="primary"
                    @click.stop="filtersVisible = true"
                  >
                    <v-icon dark>mdi-filter-variant</v-icon>
                  </v-btn>
                  <v-btn
                    class="float-right mr-1"
                    v-show="!isDrawingActive"
                    fab
                    dark
                    :title="$i18n.t('dashboard.addOffer')"
                    @click="startDrawingPolygon"
                    small
                    color="primary"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                  <v-btn
                    class="float-right mr-1"
                    fab
                    dark
                    :title="$i18n.t('dashboard.map.accept')"
                    v-show="isDrawingActive && !isFilter"
                    @click="acceptDrawedPolygon()"
                    small
                    color="primary"
                  >
                    <v-icon dark>mdi-check</v-icon>
                  </v-btn>
                  <v-btn
                    class="float-right mr-1"
                    fab
                    dark
                    v-show="isDrawingActive && !isFilter"
                    :title="$i18n.t('dashboard.map.cancel')"
                    @click="cancelDrawingPolygon()"
                    small
                    color="primary"
                  >
                    <v-icon dark>mdi-close</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="activeTab === 0"
                    fab
                    small
                    class="float-right mr-1"
                    color="primary"
                    :title="$i18n.t('dialog.header.massEditingOfAttributes')"
                    @click.stop="isFieldCalculatorVisible = true"
                  >
                    <v-icon dark>mdi-calculator-variant-outline</v-icon>
                  </v-btn>
                </v-badge>
              </span>
            </v-col>
          </v-row>
          <v-row ref="filters" class="pr-3 pl-3">
            <v-col cols="12" md="4" class="pb-0">
              <v-select
                :attach="true"
                clearable
                v-model="wojewodztwo"
                :items="provinces"
                item-text="jpt_nazwa_"
                return-object
                @change="getPRG('counties')"
                :label="$i18n.t('dashboard.offers.woj')"
                :no-data-text="$i18n.t('default.noDataText')"
                outlined
              />
            </v-col>
            <v-col cols="12" md="4" class="pb-0">
              <v-select
                :attach="true"
                clearable
                v-model="powiat"
                :items="counties"
                item-text="jpt_nazwa_"
                return-object
                @change="getPRG('communes')"
                item-value="jpt_kod_je"
                :label="$i18n.t('dashboard.offers.pow')"
                :no-data-text="$i18n.t('default.noDataText')"
                outlined
                :disabled="!wojewodztwo"
              />
            </v-col>

            <v-col cols="12" md="4" class="pb-0">
              <v-select
                :attach="true"
                clearable
                v-model="gmina"
                :items="communes"
                :label="$i18n.t('dashboard.offers.gmi')"
                outlined
                return-object
                :disabled="!powiat"
                item-text="jpt_nazwa_"
                :no-data-text="$i18n.t('default.noDataText')"
              />
            </v-col>
          </v-row>
          <v-row ref="tabs">
            <v-col class="pt-0 project" cols="12" md="12" sm="12">
              <div :title="titleProject">
                <v-btn
                  small
                  text
                  outlined
                  color="primary"
                  v-show="activeTab === 0"
                  class="project__button"
                  @click="createProject()"
                  :disabled="!isAnyOfferChecked"
                  :class="{
                    'project__button--active': isAnyOfferChecked
                  }"
                >{{ $i18n.t(`dashboard.projects.createProject`) }}</v-btn>
              </div>
            </v-col>
            <v-col cols="12" class="pt-0" md="12" sm="12">
              <v-tabs ref="tabs_1" v-model="activeTab">
                <v-tab>{{ $i18n.t('dashboard.source.cbre') }}</v-tab>
                <v-tab>{{ $i18n.t('dashboard.source.public') }}</v-tab>
                <v-tab>{{ $i18n.t('dashboard.source.projects') }}</v-tab>
                <v-tab>{{ $i18n.t('dashboard.source.owners') }}</v-tab>
              </v-tabs>
            </v-col>
          </v-row>
          <template v-if="!contentLoading && !tableLoading">
            <v-row no-gutters align="center" v-if="activeTab === 2">
              <v-col cols="auto" class="pl-3">
                <v-switch v-model="isMyProjectsFilterActive" />
              </v-col>
              <v-col
                cols="auto"
                class="text-body-2 pl-2"
              >{{$i18n.t('dashboard.projects.showMyProjects')}}</v-col>
            </v-row>
            <v-row no-gutters align="center" v-if="activeTab === 0 || activeTab === 1">
              <v-col cols="auto" class="pl-3">
                <v-switch v-model="isMapViewFilter" />
              </v-col>
              <v-col
                cols="auto"
                class="text-body-2 pl-2"
              >{{$i18n.t('dashboard.offers.toggleMapViewFilter')}}</v-col>
            </v-row>
          </template>
          <v-row ref="table" no-gutters class="overflow-y-auto">
            <template v-if="contentLoading || tableLoading">
              <v-col cols="12">
                <v-row no-gutters justify="center" align="center" style="height: 100%">
                  <v-col cols="auto">
                    <v-progress-circular indeterminate color="primary" />
                  </v-col>
                </v-row>
              </v-col>
            </template>
            <template v-else>
              <v-col cols="12" style="height: 100%;" v-if="activeTab !== 2 && activeTab !== 3">
                <v-data-table
                  id="table-offers"
                  :headers="filteredOfferTableHeaders"
                  :loading="tableLoading"
                  :items="filteredFeaturesInView"
                  :items-per-page="itemsPerPage"
                  :page="currentPage"
                  fixed-header
                  :footer-props="{
                  'items-per-page-options': [100, 200, 250]
                }"
                  v-bar
                  :no-data-text="$i18n.t('default.noDataText')"
                >
                  <template
                    v-slot:header.nazwa_nieruchomosci="{ header }"
                  >{{ $i18n.t(`dashboard.table.${header.text}`) }}</template>
                  <template v-slot:header.wojewodztwo>{{ $i18n.t('dashboard.offers.woj') }}</template>
                  <template
                    v-slot:header.pow_dzialki="{ header }"
                  >{{ $i18n.t(`dashboard.table.${header.text}`) }}</template>
                  <template class="table__column--tools" v-slot:header.tools>
                    <v-icon class="subtitle-2 pb-1">mdi-tools</v-icon>
                  </template>
                  <template v-slot:body="{ items }">
                    <tbody id="table-offers-body">
                      <tr
                        :id="`table-offer-${item.id}`"
                        v-for="item in items"
                        :key="item.id"
                        @click="selectRowByClick(item.id)"
                        :class="{
                        'table__row--background': item.id === selectedRow
                      }"
                      >
                        <td @click.stop class="checkbox__table" v-if="item.keeper === 'CBRE'">
                          <v-checkbox
                            class="project__checkbox small"
                            hide-details
                            color="primary"
                            :key="item.id"
                            v-model="ids"
                            :value="item.id"
                          />
                        </td>
                        <Attribute
                          style="max-width: 250px;"
                          :item="item.nazwa_nieruchomosci"
                          :class="{ 'name--bold': item.copied }"
                          translate
                        />
                        <Attribute :item="item.wojewodztwo" />
                        <Attribute :item="item.pow_dzialki" />
                        <td>
                          <v-icon
                            class="mr-2"
                            :title="$i18n.t(`dashboard.table.zoomToOffer`)"
                            small
                            @click="zoomToOffer(item)"
                          >mdi-magnify-plus-outline</v-icon>
                          <v-icon
                            small
                            class="mr-2"
                            :title="$i18n.t(`dashboard.table.details`)"
                            v-show="activeTab === 1"
                            @click.stop="
                            $router.push({
                              name: 'offer',
                              params: { id: item.id }
                            })
                          "
                          >mdi-eye-outline</v-icon>
                          <v-icon
                            small
                            class="mr-2"
                            @click.stop="copyPublicOffer(item.id)"
                            :title="$i18n.t(`dashboard.table.copy`)"
                            v-show="activeTab === 1"
                          >mdi-content-copy</v-icon>
                          <v-icon
                            small
                            class="mr-2"
                            v-show="activeTab === 0"
                            :title="$i18n.t(`dashboard.table.edit`)"
                            @click.stop="
                            $router.push({
                              name: 'offer',
                              params: { id: item.id }
                            })
                          "
                          >mdi-pencil</v-icon>
                          <v-icon
                            class="mr-2"
                            :title="$i18n.t(`dashboard.table.delete`)"
                            small
                            v-if="activeTab === 0"
                            @click="prepareDeleteOffer(item, false)"
                          >mdi-delete</v-icon>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-data-table>
              </v-col>

              <!-- Projects table -->
              <v-col cols="12" style="height: 100%;" v-if="activeTab === 2">
                <v-data-table
                  id="table-projects"
                  class="overflow-y-auto"
                  :headers="projectsMeta"
                  :loading="tableLoading"
                  :items="filteredProjects"
                  :single-expand="true"
                  show-expand
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  v-bar
                  @item-expanded="itemExpanded"
                  :no-data-text="$i18n.t('default.noDataText')"
                >
                  <template v-slot:header.tools>
                    <v-icon class="subtitle-2 pb-1">mdi-tools</v-icon>
                    <!-- {{ $i18n.t(`dashboard.table.${header.text}`) }} -->
                  </template>
                  <template v-slot:item.added_at="{ item }">
                    {{item.added_at | dateFilter}}
                  </template>
                  <template v-slot:item.tools="{ item }">
                    <v-icon
                      class="mr-2"
                      :title="$i18n.t(`dashboard.projects.table.delete`)"
                      small
                      @click="prepareDeleteProject(item)"
                    >mdi-delete</v-icon>
                    <v-icon
                      small
                      class="mr-2"
                      :title="$i18n.t(`dashboard.projects.table.details`)"
                      @click="
                      $router.push({ name: 'project', params: { id: item.id } })
                    "
                    >mdi-eye-outline</v-icon>
                  </template>
                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="pa-0">
                      <v-data-table
                        v-if="projectOffers && item.id === expandedItem"
                        id="table-offers"
                        class="overflow-y-auto table-offers--panel"
                        :headers="tableHeaders"
                        :loading="tableLoading"
                        :items="projectOffers"
                        disable-pagination
                        hide-default-footer
                        fixed-header
                        v-bar
                        :no-data-text="$i18n.t('default.noDataText')"
                      >
                        <template
                          v-slot:header.nazwa_nieruchomosci="{ header }"
                        >{{ $i18n.t(`dashboard.table.${header.text}`) }}</template>
                        <template v-slot:header.wojewodztwo>{{ $i18n.t('dashboard.offers.woj') }}</template>
                        <template
                          v-slot:header.pow_dzialki="{ header }"
                        >{{ $i18n.t(`dashboard.table.${header.text}`) }}</template>
                        <template v-slot:header.tools>
                          <v-icon class="subtitle-2 pb-1 align-center">mdi-tools</v-icon>

                          <!-- {{ $i18n.t(`dashboard.table.${header.text}`) }} -->
                        </template>
                        <template v-slot:body="{ items }">
                          <tbody>
                            <tr
                              v-for="item in items"
                              :key="item.id"
                              @click="selectRowByClick(item.id)"
                              :class="{
                              'table__row--background': item.id === selectedRow
                            }"
                            >
                              <!-- @click.stop - Prevent row from marking after selecting checkbox  -->
                              <td @click.stop class="checkbox__table">
                                <v-checkbox
                                  class="project__checkbox small"
                                  hide-details
                                  color="primary"
                                  :key="item.id"
                                  v-model="ids"
                                  :value="item.id"
                                />
                              </td>
                              <Attribute
                                :item="item.nazwa_nieruchomosci"
                                :class="{ 'name--bold': item.copied }"
                                translate
                              />
                              <Attribute :item="item.wojewodztwo" />
                              <Attribute :item="item.pow_dzialki" />
                              <td class="d-flex justify-center">
                                <v-icon
                                  small
                                  class="mr-2"
                                  :title="
                                  $i18n.t(
                                    `dashboard.projects.table.editDescription`
                                  )
                                "
                                  @click="addDescription(item)"
                                >mdi-textbox</v-icon>
                                <v-icon
                                  small
                                  class="mr-2"
                                  :title="$i18n.t(`dashboard.table.details`)"
                                  @click="
                                  $router.push({
                                    name: 'offer',
                                    params: { id: item.id }
                                  })
                                "
                                >mdi-eye-outline</v-icon>
                                <v-icon
                                  class="mr-2"
                                  :title="$i18n.t(`dashboard.table.zoomToOffer`)"
                                  small
                                  @click="zoomToOffer(item)"
                                >mdi-magnify-plus-outline</v-icon>
                                <v-icon
                                  class="mr-2"
                                  :title="$i18n.t(`dashboard.table.delete`)"
                                  small
                                  @click="prepareDeleteOffer(item, true)"
                                >mdi-delete</v-icon>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-data-table>
                      <v-row class="align-center justify-center" v-else>
                        <v-col cols="8">
                          <v-progress-linear indeterminate rounded color="primary accent-4" />
                        </v-col>
                      </v-row>
                    </td>
                  </template>
                </v-data-table>
              </v-col>
              <v-col cols="12" style="height: 100%;" v-if="activeTab === 3">
                <v-data-table
                  id="table-owners"
                  class="overflow-y-auto"
                  :headers="ownersMeta"
                  :loading="tableLoading"
                  :items="filteredOwners"
                  :single-expand="true"
                  item-key="id"
                  show-expand
                  disable-pagination
                  hide-default-footer
                  fixed-header
                  v-bar
                  @item-expanded="ownerExpanded"
                  :no-data-text="$i18n.t('default.noDataText')"
                >
                  <template
                    v-slot:header.wlasciciel.pl="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.wlasciciel.en="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.adres="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.email="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template
                    v-slot:header.telefon="{header}"
                  >{{ $i18n.t(`dashboard.owners.table.${header.text}`) }}</template>
                  <template v-slot:expanded-item="{ headers, item }">
                    <td :colspan="headers.length" class="pa-0">
                      <v-data-table
                        v-if="item.id === expandedItem"
                        id="table-offers"
                        class="overflow-y-auto table-offers--panel"
                        :headers="ownerOffersHeaders"
                        :loading="tableLoading"
                        :items="ownerOffers"
                        disable-pagination
                        hide-default-footer
                        fixed-header
                        v-bar
                        :no-data-text="$i18n.t('default.noDataText')"
                      >
                        <template
                          v-slot:header.nazwa_nieruchomosci="{ header }"
                        >{{ $i18n.t(`dashboard.table.${header.text}`) }}</template>
                        <template v-slot:header.wojewodztwo>{{ $i18n.t('dashboard.offers.woj') }}</template>
                        <template
                          v-slot:header.pow_dzialki="{ header }"
                        >{{ $i18n.t(`dashboard.table.${header.text}`) }}</template>
                        <template v-slot:header.tools>
                          <v-icon class="subtitle-2 pb-1 align-center">mdi-tools</v-icon>
                        </template>
                        <template v-slot:body="{ items }">
                          <tbody>
                            <tr
                              v-for="item in items"
                              :key="item.id"
                              @click="selectRowByClick(item.id)"
                              :class="{
                              'table__row--background': item.id === selectedRow
                            }"
                            >
                              <Attribute
                                :item="item.nazwa_nieruchomosci"
                                :class="{ 'name--bold': item.copied }"
                                translate
                              />
                              <Attribute :item="item.wojewodztwo" />
                              <Attribute :item="item.pow_dzialki" />
                              <td class="d-flex justify-center">
                                <v-icon
                                  small
                                  class="mr-2"
                                  :title="$i18n.t(`dashboard.table.details`)"
                                  @click="
                                  $router.push({
                                    name: 'offer',
                                    params: { id: item.id }
                                  })
                                "
                                >mdi-eye-outline</v-icon>
                                <v-icon
                                  class="mr-2"
                                  :title="$i18n.t(`dashboard.table.zoomToOffer`)"
                                  small
                                  @click="zoomToOffer(item)"
                                >mdi-magnify-plus-outline</v-icon>
                              </td>
                            </tr>
                          </tbody>
                        </template>
                      </v-data-table>
                    </td>
                  </template>
                </v-data-table>
              </v-col>
            </template>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" md="6" style="height: inherit">
        <v-card height="100%">
          <v-row style="height: 100%" no-gutters>
            <v-col class="pa-1" style="height: inherit">
              <div style="height:100%; width:100%">
                <DashboardMap
                  ref="map"
                  :offers="offersFiltered"
                  @onSelectedId="selectRowById"
                  @clearSelection="clearSelection"
                  @filteredFeaturesWithGeometry="setFeaturesInGeometry"
                  @filteredFeaturesWithView="setFeaturesInView"
                  @cancelFiltering="filteredOffers = []"
                  :isFilter.sync="isFilter"
                  :isDrawingActive.sync="isDrawingActive"
                />
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <FieldFilter
      :is-visible.sync="filtersVisible"
      :entities="filtersConfig.entities"
      :entitiesType="filtersConfig.entitiesType"
      :fields="filtersConfig.properties"
      :public-hints="filtersConfig.publicHints"
      @setFiltersBadge="filtersLength = $event"
      @setFilteredEntities="setFilteredEntities"
    />
    <ConfirmDialog
      v-if="confirmDialogLoaded"
      :dialog="confirmDialogVisible"
      @success="dialogConfig[currentDialogContent].successFunction"
      @cancel="dialogConfig[currentDialogContent].cancelFunction"
    >
      <template v-slot:header>{{ dialogConfig[currentDialogContent].header }}</template>
      <template v-slot:content>
        <p v-if="isConfirmDialog">{{ dialogConfig[currentDialogContent].content }}</p>
        <v-textarea v-else maxlength="100" v-model="description" outlined counter />
      </template>
    </ConfirmDialog>
    <CreateProjectDialog
      v-if="isCreateProjectDialogLoaded"
      :is-visible="isCreateProjectDialogVisible"
      :is-new-offer="isNewOffer"
      :ids="ids"
      @close="isCreateProjectDialogVisible = false"
    />
    <FieldCalculator
      v-if="isFieldCalculatorVisible"
      :features-ids="filteredOffersIds"
      :is-visible.sync="isFieldCalculatorVisible"
      :content-text="$i18n.t('dialog.content.allFilteredOffersWillBeEdited', { length: filteredOffersIds.length })"
      entity="offers"
      @success="reloadOffers"
    />
  </v-container>
</template>

<script>
import DashboardMap from '@/components/dashboard/DashboardMap';
import { wojOptions } from '@/models/Offer';
import Attribute from '@/components/dashboard/Attribute';
import { GeoJSON } from 'ol/format';
import AddNewFeatureDialog from '@/components/dashboard/AddNewFeatureDialog';
import FeatureDialog from '@/components/dashboard/FeatureDialog';
import FieldFilter from '@/components/FieldFilter';

// mixins
import tabsMixin from '@/mixins/tabsMixin';
import sortMixin from '@/mixins/sortMixin';

const TYPE = function(obj) {
  return {}.toString
    .call(obj)
    .match(/\s([a-zA-Z]+)/)[1]
    .toLowerCase();
};

export default {
  name: 'Dashboard',
  mixins: [tabsMixin, sortMixin],
  components: {
    DashboardMap,
    Attribute,
    CreateProjectDialog: () => import('@/components/CreateProjectDialog'),
    ConfirmDialog: () => import('@/components/ConfirmDialog'),
    FieldCalculator: () => import('@/components/FieldCalculator'),
    FieldFilter
  },
  data: vm => ({
    isFieldCalculatorVisible: false,
    currentDialogContent: 'deleteOffer',
    isPrintingVisible: false,
    isNewOffer: false,
    tableLoading: true,
    filtersVisible: false,
    filtersLength: null,
    parcelsToAdd: [],
    selectedRow: undefined,
    currentPage: 1,
    itemsPerPage: 100,
    isMyProjectsFilterActive: true,
    tableHeaders: [
      {
        text: null,
        value: 'selectable',
        type: 'text',
        sortable: false
      },
      {
        text: 'nazwa_nieruchomosci',
        value: 'nazwa_nieruchomosci',
        type: 'text',
        translate: true
      },
      {
        text: 'wojewodztwo',
        value: 'wojewodztwo',
        type: 'text'
      },
      {
        text: 'pow_dzialki',
        value: 'pow_dzialki',
        type: 'number'
      },
      {
        text: 'tools',
        value: 'tools',
        align: 'center',
        sortable: false
      }
    ],
    ownerOffersHeaders: [
      {
        text: 'nazwa_nieruchomosci',
        value: 'nazwa_nieruchomosci',
        type: 'text',
        translate: true
      },
      {
        text: 'wojewodztwo',
        value: 'wojewodztwo',
        type: 'text'
      },
      {
        text: 'pow_dzialki',
        value: 'pow_dzialki',
        type: 'number'
      },
      {
        text: 'tools',
        value: 'tools',
        align: 'center',
        sortable: false
      }
    ],
    wojewodztwo: undefined,
    powiat: undefined,
    gmina: undefined,
    offers: [],
    activeTab: 0,
    ids: [],
    filteredOffers: [],
    featuresInView: [],
    itemToDelete: undefined,
    map: undefined,
    expandedItem: null,
    description: '',

    // Component settings variables
    confirmDialogLoaded: false,
    confirmDialogVisible: false,
    // isLegendActive: false,

    isConfirmDialog: false,
    isCreateProjectDialogLoaded: false,
    isCreateProjectDialogVisible: false,
    isDrawingActive: false,
    isFilter: undefined,

    contentLoading: true,

    //PRG
    provinces: [],
    counties: [],
    communes: [],
    cbreOfferAttributes: [],
    publicOfferAttributes: [],

    owners: [],
    filteredOwners: [],
    ownerOffers: [],
    ownersMeta: [
      {
        text: 'ownerPl',
        value: 'wlasciciel.pl',
        type: 'text'
      },
      {
        text: 'ownerEn',
        value: 'wlasciciel.en',
        type: 'text'
      },
      {
        text: 'address',
        value: 'adres',
        type: 'text'
      },
      {
        text: 'email',
        value: 'email',
        type: 'text'
      },
      {
        text: 'phone',
        value: 'telefon',
        type: 'text'
      }
    ],

    // projects
    projectsMeta: [
      {
        text: 'PL',
        type: 'text',
        value: 'title.pl'
      },
      {
        text: 'EN',
        type: 'text',
        value: 'title.en'
      },
      {
        text: vm.$i18n.t('project.table.added_at'),
        type: 'date',
        value: 'added_at',
        sort: (a, b) => vm.sortDates(a, b)
      },
      {
        text: 'tools',
        value: 'tools',
        align: 'center',
        sortable: false
      }
    ],
    projects: [],
    userProjects: [],
    filteredProjects: [],
    projectOffers: [],
    offersFilteredInModal: [],
    offersHistory: [],
    offersHistoryLoading: true,
    offersPage: 0,
    unreadOffersCount: 0,
    isOffersAttributesFetched: false
  }),
  watch: {
    activeTab(nV) {
      this.contentLoading = true;
      setTimeout(() => {
        this.contentLoading = false;
      }, 1500);
      if (nV < 2) {
        setTimeout(() => {
          this.$refs.map.getFeaturesInView();
        }, 0);
      }
    },
    wojewodztwo(newVal) {
      if (!newVal) {
        this.gmina = undefined;
        this.powiat = undefined;
        this.$store.commit('setOfferWoj', undefined);
        this.$store.commit('setOfferPowiat', undefined);
        this.$store.commit('setOfferGmina', undefined);
      } else {
        this.$store.commit('setOfferWoj', newVal);
        this.$store.commit('setOfferPowiat', undefined);
        this.$store.commit('setOfferGmina', undefined);
      }
    },
    powiat(newVal) {
      if (!newVal) {
        this.gmina = undefined;
        this.$store.commit('setOfferPowiat', undefined);
        this.$store.commit('setOfferGmina', undefined);
      } else {
        this.$store.commit('setOfferPowiat', newVal);
        this.$store.commit('setOfferGmina', undefined);
      }
    },
    gmina(newVal) {
      if (!newVal) {
        this.$store.commit('setOfferGmina', undefined);
      } else {
        this.$store.commit('setOfferGmina', newVal);
      }
    }
  },
  computed: {
    isMapViewFilter: {
      get() {
        return this.$store.getters.getOffersMapViewFilter;
      },
      set(nV) {
        this.$store.commit('setOffersMapViewFilter', nV);
      }
    },
    computedProjects() {
      return this.isMyProjectsFilterActive ? this.userProjects : this.projects;
    },
    filteredOffersIds() {
      return this.filteredFeaturesInView.map(feature => feature.id);
    },
    filteredOfferTableHeaders() {
      if (this.activeTab === 0) {
        return this.tableHeaders;
      }
      return [...this.tableHeaders].slice(1, this.tableHeaders.length);
    },
    filtersConfig() {
      if (this.activeTab === 0 || this.activeTab === 1) {
        return {
          entitiesType: 'offers',
          entities: this.offersFiltered,
          properties: this.activeTab ? this.publicOfferAttributes : this.cbreOfferAttributes,
          publicHints: this.activeTab === 1
        };
      } else if (this.activeTab === 2) {
        return {
          entitiesType: 'projects',
          entities: this.computedProjects,
          properties: this.projectsMeta
            .filter(meta => !['tools', 'added_at'].includes(meta.value))
            .map(header => {
              const { text, translate, type, value } = header;
              return {
                name: value,
                type,
                translate,
                label_pl: header.text,
                label_en: header.text
              };
            })
        };
      } else {
        return {
          entitiesType: 'owners',
          entities: this.owners,
          properties: this.ownersMeta.map(header => {
            const { text, translate, type, value } = header;
            return {
              name: value,
              type,
              translate,
              label_pl: this.$i18n.t(`dashboard.owners.table.${header.text}`),
              label_en: this.$i18n.t(`dashboard.owners.table.${header.text}`)
            };
          })
        };
      }
    },
    dialogConfig() {
      return {
        deleteOffer: {
          header: this.$i18n.t('dialog.header.deleteOffer'),
          content: this.$i18n.t('dialog.content.deleteOffer'),
          successFunction: this.deleteOffer,
          cancelFunction: this.deleteConfirmCancel
        },
        deleteProjectOffer: {
          header: this.$i18n.t('dialog.header.deleteProjectOffer'),
          content: this.$i18n.t('dialog.content.deleteProjectOffer'),
          successFunction: this.deleteProjectOffer,
          cancelFunction: this.deleteConfirmCancel
        },
        editDescription: {
          header: this.$i18n.t('dialog.header.editOfferDescription'),
          successFunction: this.acceptDescription,
          cancelFunction: this.cancelDescription
        },
        deleteProject: {
          header: this.$i18n.t('dialog.header.deleteProject'),
          content: this.$i18n.t('dialog.content.deleteProject'),
          successFunction: this.deleteProject,
          cancelFunction: this.deleteConfirmCancel
        }
      };
    },
    wojOptions() {
      return [{ value: '', name: this.$i18n.t('dashboard.offers.all') }].concat(wojOptions);
    },
    offersFiltered() {
      if (this.offers.length === 0) return [];
      let offersFiltered = [...this.offers];
      if (this.activeTab === 0) {
        offersFiltered = offersFiltered.filter(g => g.keeper === 'CBRE');
      } else if (this.activeTab === 1) {
        offersFiltered = offersFiltered.filter(g => g.keeper !== 'CBRE');
      }
      offersFiltered = this.filterByPRG('wojewodztwo', offersFiltered);
      offersFiltered = this.filterByPRG('powiat', offersFiltered);
      offersFiltered = this.filterByPRG('gmina', offersFiltered);
      if (this.filteredOffers.length > 0) {
        offersFiltered = offersFiltered.filter(offer => this.filteredOffers.includes(offer.id));
      }
      // if (this.$refs.map) {
      //   this.clearSelection(); // just in case
      // }
      return offersFiltered;
    },
    filteredFeaturesInView() {
      return this.isMapViewFilter
        ? this.offersFilteredInModal.filter(f => this.featuresInView.includes(f.id))
        : this.offersFilteredInModal;
    },
    isAnyOfferChecked() {
      return this.ids.length > 0;
    },
    titlePPTX() {
      return this.isAnyOfferChecked
        ? this.$i18n.t(`dashboard.table.getPPTX`)
        : this.$i18n.t(`dashboard.table.chooseOfferPPTX`);
    },
    titleXLSX() {
      return this.isAnyOfferChecked
        ? this.$i18n.t(`dashboard.table.getXLSX`)
        : this.$i18n.t(`dashboard.table.chooseOfferXLSX`);
    },
    titleProject() {
      return this.isAnyOfferChecked
        ? this.$i18n.t(`dashboard.projects.createProject`)
        : this.$i18n.t(`dashboard.projects.chooseOfferProject`);
    },
    getCardGrid() {
      if (this.activeTab != 3 && !this.contentLoading && !this.tableLoading) {
        return 'grid-template-rows: 55px 100px 100px 50px 1fr;';
      } else {
        return 'grid-template-rows: 55px 100px 100px 1fr;';
      }
    },
    wmsLayers() {
      return this.$store.getters.getWMSLayers;
    }
    // projectOffers() {}
  },
  methods: {
    // Display offer description modal
    addDescription(item) {
      this.currentDialogContent = 'editDescription';
      this.item = item;
      if (item.hasOwnProperty('description')) {
        this.description = item.description;
      }
      this.confirmDialogLoaded = true;
      this.confirmDialogVisible = true;
      this.isConfirmDialog = false;
    },
    // Accept new offer description
    acceptDescription() {
      this.$store
        .dispatch('editProjectOffer', {
          project: this.expandedItem,
          offer: this.item.id,
          description: this.description
        })
        .then(() => {
          this.item.description = this.description;
          this.description = '';
          this.confirmDialogVisible = false;
          this.isConfirmDialog = false;
          this.$root.$emit('showSnackbar', this.$i18n.t(`default.editedProjectDescription`));
        })
        .catch(e => {
          this.$root.$emit('showSnackbar', e);
        });
    },
    // Wykonaj odpowiednią akcję z geometrią narysowanego poligonu
    acceptDrawedPolygon() {
      if (this.isFilter) {
        this.$refs.map.filterWithArea();
      } else {
        this.$refs.map.filterWithArea('newOffer');
      }
    },
    addNewOffer() {
      this.$refs.map.setViewData();
      this.$router.push({ name: 'offer', params: { id: 'new' } });
    },
    // Cancel adding description
    cancelDescription() {
      this.description = '';
      this.confirmDialogVisible = false;
    },
    // Wyłącz narzędzie rysowania poligonu
    cancelDrawingPolygon() {
      // if (!this.isFilter) {
      //   this.$refs.map.getLayerByName('offers').setVisible(true);
      //   this.$refs.map.getLayerByName('parcels').setVisible(false);
      // }
      this.isFilter = false;
      this.isDrawingActive = false;
      this.$refs.map.cancelDrawingPolygon();
      // this.filteredOffers = [];
    },
    // changeBaselayer(baselayer) {
    //   this.$refs.map.changeBaselayer(baselayer);
    // },
    copyPublicOffer(id) {
      //let obj = this.offersFiltered.find(offer => offer.id === id);
      //obj.owner = "CBRE";
      //obj.copied = true;
      this.$store.dispatch('postPublicOfferToCopy', id).then(r => {
        if (r.status === 200 || r.status === 201) {
          this.$root.$emit('showSnackbar', this.$i18n.t('offer.offers.copy200'));
          this.$router.push(`/offer/${r.data.offers}`);
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t('offer.offers.errorDB'));
        }
      });
    },
    clearSelection() {
      this.selectedRow = undefined;
      this.$refs.map.selectOfferById(undefined);
    },
    createProject() {
      this.isCreateProjectDialogLoaded = true;
      this.isCreateProjectDialogVisible = true;
    },
    createPPTX() {
      let payload = {
        ids: this.ids
      };
      this.$store.dispatch('createPPTX', payload).then(() => {
        this.ids = [];
        this.clearSelection();
      });
    },
    createXLSX() {
      let payload = {
        ids: this.ids
      };
      this.$store.dispatch('createXLSX', payload).then(() => {
        this.ids = [];
        this.clearSelection();
      });
    },
    deleteConfirmCancel() {
      this.confirmDialogVisible = false;
    },
    deleteOffer() {
      this.$store.dispatch('deleteOffer', this.itemToDelete.id).then(r => {
        if (r.status === 200) {
          const index = this.offers.indexOf(this.itemToDelete);
          this.offers.splice(index, 1);
          this.$root.$emit('showSnackbar', this.$i18n.t('default.successfullyDeletedOffer'));
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t('offer.offers.errorDB'));
        }
      });
      this.confirmDialogVisible = false;
    },
    deleteProject() {
      this.$store.dispatch('deleteProject', this.itemToDelete.id).then(r => {
        if (r.status === 200) {
          const index = this.projects.indexOf(this.itemToDelete);
          this.projects.splice(index, 1);
          this.$root.$emit('showSnackbar', this.$i18n.t('default.successfullyDeletedProject'));
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t('offer.offers.errorDB'));
        }
      });
      this.confirmDialogVisible = false;
    },
    deleteProjectOffer() {
      this.$store
        .dispatch('deleteProjectOffer', {
          project: this.expandedItem,
          offer: this.itemToDelete.id
        })
        .then(() => {
          const index = this.projectOffers.indexOf(this.itemToDelete);
          this.projectOffers.splice(index, 1);
          this.confirmDialogVisible = false;
          this.$root.$emit('showSnackbar', this.$i18n.t(`default.successfullyDeletedProjectOffer`));
        })
        .catch(e => {
          this.$root.$emit('showSnackbar', e);
        });
    },
    filterWithArea() {
      this.$refs.map.filterWithArea();
    },
    filterByPRG(level, offers) {
      if (this[level] != undefined) {
        return offers.filter(g => {
          return g[level] == this[level].jpt_nazwa_;
        });
      }
      return offers;
    },
    getOffersHistory() {
      if (this.offersPage === 'end') {
        return;
      }
      this.$store.dispatch('getOffersHistory', this.offersPage).then(r => {
        if (r.status === 200) {
          this.offersHistory = this.offersHistory.concat(r.data.data);
          this.offersHistoryLoading = false;
          this.offersPage += 1;
        } else if (r.response.status === 404) {
          this.offersPage = 'end';
          this.offersHistoryLoading = false;
        }
      });
    },
    getUnreadCount() {
      this.$store.dispatch('getUnreadCount').then(r => {
        if (r.status === 200) {
          this.unreadOffersCount = r.data.data;
          this.getOffersHistory();
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t('default.unreadCountError'));
        }
      });
    },
    getPRG(level) {
      const woj = this.wojewodztwo ? this.wojewodztwo.jpt_kod_je : null;
      const pow = this.powiat ? this.powiat.jpt_kod_je : null;
      let payload = {
        level,
        params: {
          woj,
          pow
        }
      };
      if (woj) {
        this.$store.dispatch('getPRG', payload).then(r => {
          if (r.status === 200) {
            this[level] = r.data[level];
          }
        });
      }
    },
    prepareDeleteOffer(item, fromProject) {
      this.currentDialogContent = fromProject ? 'deleteProjectOffer' : 'deleteOffer';
      this.confirmDialogLoaded = true;
      this.confirmDialogVisible = true;
      this.isConfirmDialog = true;
      this.itemToDelete = item;
    },
    prepareDeleteProject(item) {
      this.currentDialogContent = 'deleteProject';
      this.confirmDialogLoaded = true;
      this.confirmDialogVisible = true;
      this.isConfirmDialog = true;
      this.itemToDelete = item;
    },
    itemExpanded(e) {
      if (e.value) {
        this.$store
          .dispatch('getProjectOffers', { project: e.item.id })
          .then(r => {
            this.projectOffers = r.data.offers.map(offer => {
              return this.offers.find(o => o.id === offer);
            });
            setTimeout(() => {
              this.expandedItem = e.item.id;
            }, 800);
          })
          .catch(e => {
            this.$root.$emit('showSnackbar', e);
          });
      }
    },
    markAsReadSingle(offer, push) {
      if (offer.read && push) {
        this.$router.push(`/offer/${offer.offer_id}`);
      } else if (!offer.read) {
        this.$store.dispatch('markAsReadSingle', offer.history_id).then(r => {
          if (r.status === 204) {
            if (push) {
              this.$router.push(`/offer/${offer.offer_id}`);
            } else {
              this.offersHistory.find(h => h.history_id === offer.history_id).read = true;
              this.unreadOffersCount -= 1;
            }
          } else {
            this.$root.$emit('showSnackbar', this.$i18n.t(`default.notificationError`));
          }
        });
      }
    },
    markAsReadAll() {
      this.$store.dispatch('markAsReadAll').then(r => {
        if (r.status === 204) {
          for (let idx in this.offersHistory) {
            this.offersHistory[idx].read = true;
            this.unreadOffersCount = 0;
          }
        }
      });
    },
    offerNotificationSubtitle(date) {
      return date.split('T')[0];
    },
    offerNotificationTitle(offer) {
      return (
        `${offer.nazwa_nieruchomosci[this.$i18n.locale]} [${offer.offer_keeper}] ` +
        this.$i18n.t(`dashboard.offers.${offer.action}`)
      );
    },
    ownerExpanded(e) {
      if (e.value) {
        this.ownerOffers = [];
        this.ownerOffers = this.offers.filter(offer => {
          const owners = offer.wlasciciele
            ? offer.wlasciciele.map(wlasciciel => (wlasciciel ? wlasciciel.pl : null))
            : [];
          return owners.includes(e.item.wlasciciel.pl);
        });
        this.expandedItem = e.item.id;
      }
    },
    print() {
      this.isPrintingVisible = false;
    },
    runFilter(data) {
      this.filters = data;
      this.filtersVisible = false;
    },
    scroll(e) {
      if (e.srcElement.scrollTop >= e.srcElement.scrollHeight - e.srcElement.clientHeight) {
        this.getOffersHistory();
      }
    },
    selectRowByClick(id) {
      if (this.selectedRow == id) {
        id = undefined;
      }
      this.selectedRow = id;
      this.$refs.map.selectOfferById(id);
    },
    selectRowById(id) {
      this.selectedRow = id;
      if (this.selectedRow != undefined) {
        const position = this.offersFiltered.map(i => i.id).indexOf(id);
        this.currentPage = Math.floor(position / this.itemsPerPage) + 1;
        this.$nextTick(() => {
          const selectedRowNode = document.getElementById(`table-offer-${id}`);
          selectedRowNode.scrollIntoView({
            block: 'center'
          });
        });
      }
    },
    setFeaturesInGeometry(offers) {
      const off = [...new Set(offers)];
      if (this.isFilter) {
        this.filteredOffers = off;
      } else {
        this.isNewOffer = true;
        this.ids = off;
        if (off.length > 0) {
          this.isCreateProjectDialogVisible = true;
          this.isCreateProjectDialogLoaded = true;
        } else {
          this.$root.$emit('showSnackbar', this.$i18n.t(`offer.parcels.chooseParcel`));
        }
      }
    },
    setFeaturesInView(features) {
      this.featuresInView = features;
    },
    // Włącz narzędzie rysowania geometrii (dla narzędzia filtrowania i rysowania działki)
    startDrawingPolygon() {
      // if (!this.isFilter) {
      //   if (this.parcelsToAdd.length === 0) {
      //     this.$root.$emit(
      //       'showSnackbar',
      //       this.$i18n.t(`default.downloadingParcels`)
      //     );
      //     this.$store.dispatch('getParcels').then(data => {
      //       for (let parcel of data) {
      //         if (parcel) {
      //           let feature = new GeoJSON().readFeature(parcel.geometry, {
      //             dataProjection: 'EPSG:4326',
      //             featureProjection: 'EPSG:3857'
      //           });
      //           feature.set('id', parcel.id);
      //           this.parcelsToAdd.push(feature);
      //         }
      //         this.$refs.map
      //           .getLayerByName('parcels')
      //           .getSource()
      //           .clear();
      //         this.$refs.map
      //           .getLayerByName('parcels')
      //           .getSource()
      //           .addFeatures(this.parcelsToAdd);
      //         this.$root.$emit(
      //           'showSnackbar',
      //           this.$i18n.t(`dashboard.drawOffer`)
      //         );
      //       }
      //     });
      //   }
      //   this.$refs.map.getLayerByName('offers').setVisible(false);
      //   this.$refs.map.getLayerByName('parcels').setVisible(true);
      // }
      this.isDrawingActive = true;
      this.isFilter = false;
      this.$refs.map.startDrawingPolygon(false);
    },
    zoomToOffer(item) {
      this.$refs.map.zoomToOfferById(item.id);
    },
    setFilteredEntities(data) {
      if (this.activeTab === 0 || this.activeTab === 1) {
        this.offersFilteredInModal = data;
      } else if (this.activeTab === 2) {
        this.filteredProjects = data;
      } else {
        this.filteredOwners = data;
      }
    },
    reloadOffers() {
      this.tableLoading = true;
      this.contentLoading = true;
      this.$store
        .dispatch('getOffers', {
          all_attributes: true,
          geometry: true
        })
        .then(data => {
          this.offers = data;
          this.tableLoading = false;
          this.contentLoading = false;
        });
    }
  },
  mounted() {
    this.$store.commit('setEditingLayer', undefined);
    this.offers = [];
    this.$store
      .dispatch('getPRG', {
        level: 'provinces'
      })
      .then(r => {
        this.provinces = r.data.provinces;
      });

    if (this.$store.getters.getOfferWoj) {
      this.wojewodztwo = this.$store.getters.getOfferWoj;
      this.getPRG('counties');
    }
    if (this.$store.getters.getOfferPowiat) {
      this.powiat = this.$store.getters.getOfferPowiat;
      this.getPRG('communes');
    }
    if (this.$store.getters.getOfferGmina) {
      this.gmina = this.$store.getters.getOfferGmina;
    }
    // if (this.$store.getters.getOfferFilters) {
    //   this.filters = this.$store.getters.getOfferFilters;
    // }
    this.$store
      .dispatch('getOffers', {
        all_attributes: true,
        geometry: true
      })
      .then(data => {
        console.log(`mounted -> data`, data);
        this.offers = data;
        this.featuresInView = data.map(el => el.id);
        this.tableLoading = false;
        this.contentLoading = false;
      });

    this.$store.dispatch('getAttributesSchema', { entity: 'offers' }).then(r => {
      if (r.status === 200) {
        const flatCbreProperties = [];
        const flatPublicProperties = [];
        for (let group in r.data.data) {
          r.data.data[group].forEach(property => {
            flatPublicProperties.push(property);
            if (group !== 'public_data') {
              flatCbreProperties.push(property);
            }
          });
        }
        this.cbreOfferAttributes = flatCbreProperties;
        this.publicOfferAttributes = flatPublicProperties;
      }
    });
    this.$store.dispatch('getProjects').then(r => {
      if (r.status === 200) {
        this.projects = r.data.projects;
        this.tableLoading = false;
      }
    });
    this.$store.dispatch('getUserProjects').then(r => {
      if (r.status === 200) {
        this.userProjects = r.data.projects;
        this.tableLoading = false;
      }
    });
    this.$store.dispatch('getOwners').then(r => {
      if (r.status === 200) {
        this.owners = r.data.owners;
        this.tableLoading = false;
      }
    });
    this.getUnreadCount();
  }
};
</script>

<style lang="scss">
html {
  overflow: hidden;
}
.v-application--is-ltr
  .v-tabs-bar.v-tabs-bar--is-mobile:not(.v-tabs-bar--show-arrows)
  > .v-slide-group__wrapper
  > .v-tabs-bar__content
  > .v-tab:first-child,
.v-application--is-ltr
  .v-tabs-bar.v-tabs-bar--is-mobile:not(.v-tabs-bar--show-arrows)
  > .v-slide-group__wrapper
  > .v-tabs-bar__content
  > .v-tabs-slider-wrapper
  + .v-tab {
  margin-left: 0;
}

div.v-data-table__wrapper.vb-content {
  height: inherit !important;
}
#project-card {
  display: grid;
  grid-template-columns: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 0px;
}
#table-offers {
  display: grid;
}
#table-offers:not(.table-offers--panel),
#table-projects {
  height: 100%;
}
#table-projects > div > table > tbody > tr > td {
  overflow: hidden;
  max-width: 300px;
  word-wrap: break-word;
}
#table-owners {
  height: 100%;
}
#table-owners > div > table > tbody > tr > td {
  overflow: hidden;
  max-width: 300px;
  word-wrap: break-word;
}
.project {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 26px;
}
.project__button--cancel {
  margin-right: 24px;
}
.project__checkbox {
  height: 30px;
  width: 10px;
  margin-top: 0;
}
.name--bold {
  font-weight: bolder;
}
.notRead {
  color: rgb(180, 0, 0);
}
.project__button--active {
  border: 1px solid var(--v-primary-base) !important;
}
.project__button--disabled {
  background-color: rgba(0, 142, 132, 0.4) !important;
  pointer-events: none !important;
}
.btn--orange--disabled {
  background-color: rgba(228, 115, 54, 0.4) !important;
}
.btn--excel--disabled {
  background-color: rgba(77, 163, 98, 0.4) !important;
}
.v-content__wrap {
  overflow-y: auto;
}
::-webkit-scrollbar {
  width: 7px;
}
::-webkit-scrollbar:hover ::-webkit-scrollbar {
  width: 12px;
}
::-webkit-scrollbar-thumb {
  background-color: rgba($primary-color, 0.2);
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background-color: rgba($primary-color, 0.5);
  width: 12px;
}
.table__row--background {
  background-color: rgba($primary-color, 0.2) !important;
}
.v-data-table tbody tr.v-data-table__expanded__content {
  box-shadow: none;
}
.wrap-text {
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
